import classnamesBind from 'classnames/bind';
import Link from '@concur/nui-widgets/lib/Link/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { withThemeStyles } from '@concur/ui-theming';
import withNuiFormatter from '../utils/withNuiFormatter';
import ChinaICP from './_ChinaICP';
import CookiePreferences from './_CookiePreferences';
import FooterText from './_FooterText';
import LastLogin from './_LastLogin';
import { getDataCenter } from '../utils/dataCenters';
import withErrorBoundary from '../utils/withErrorBoundary';
import compose from '../utils/compose';
import isFiori from '../utils/isFiori';
import * as styles from './AppFooter-*.css';

const CHINA_DATA_CENTER = 'china';
const CSS_BLOCK = 'sapcnqr-app-footer';

/* eslint-disable react/no-danger */
const Footer = (props) => {
    const {
        brandingId,
        classNameMap,
        country,
        dataCenterLocation,
        demo,
        formatter,
        isGov,
        isLoggedIn,
        isTravelUser,
        lang,
        lastLogin,
        lastSigninDateString,
        location: { pathname } = {},
        partnerLogo,
        showOuttaskDevBar,
        stretchContent,
        supportLink,
        themeName,
        useFullWidth,
        useSmallViewportBreakpoints,
    } = props;

    const classnames = classnamesBind.bind(classNameMap);

    const dataCenter = getDataCenter(dataCenterLocation);
    const showCookieConsent = !isGov;
    const showPrivacyStatementLink = !isGov;
    const showChinaLicense = dataCenter === CHINA_DATA_CENTER
        && !isLoggedIn
        && (!pathname || (pathname.indexOf('/profile') === -1 && pathname.indexOf('/v2/forgot') === -1));

    const classes = classnames(
        CSS_BLOCK,
        {
            [`${CSS_BLOCK}--full-width`]: useFullWidth,
            [`${CSS_BLOCK}--fully-responsive`]: useSmallViewportBreakpoints,
            [`${CSS_BLOCK}--china`]: showChinaLicense,
        },
    );

    const sectionClasses = classnames(
        `${CSS_BLOCK}__section`,
        {
            [`${CSS_BLOCK}__section--stretch`]: stretchContent,
        },
    );

    const logoSectionClasses = classnames(
        `${CSS_BLOCK}__section`,
        `${CSS_BLOCK}__section--logo`,
        {
            [`${CSS_BLOCK}__section--stretch`]: stretchContent,
        },
    );

    const sapLogoClasses = classnames(
        `${CSS_BLOCK}__sap`,
        {
            [`${CSS_BLOCK}__sap--fiori`]: isFiori(brandingId, themeName),
        },
    );

    const sapConcurLogoClasses = classnames(
        `${CSS_BLOCK}__sap-concur`,
        {
            [`${CSS_BLOCK}__sap-concur--fiori`]: isFiori(brandingId, themeName),
        },
    );

    const getServiceStatusUrl = () => `//open.concur.com/#${dataCenter}`;
    const getPrivacyPolicyUrl = () => {
        const langPrefix = lang ? lang.substring(0, 2) : 'en';
        switch (langPrefix) {
        case 'fr':
            return '//www.concur.fr/processor-privacy-statement';
        case 'de':
            return '//www.concur.de/processor-privacy-statement';
        case 'it':
            return '//www.concur.it/processor-privacy-statement';
        default:
            return '//www.concur.com/en-us/processor-privacy-statement';
        }
    };

    const sapConcurLogoId = isFiori(brandingId, themeName) ? 'app-footer-logo-sap-concur-fiori' : 'app-footer-logo-sap-concur';

    return (
        <div className={classes}>
            <section className={logoSectionClasses} data-test="app-footer-logos">
                <div className={sapLogoClasses} id="app-footer-logo-sap">
                    <span className={classnames(`${CSS_BLOCK}__screen-reader`)}>SAP</span>
                    <i aria-hidden="true" className={classnames('logomark-sap')} />
                </div>
                <div className={sapConcurLogoClasses}>
                    {!partnerLogo && (
                        <>
                            <span className={classnames(`${CSS_BLOCK}__screen-reader`)}>SAP Concur</span>
                            <i aria-hidden="true" className={classnames('logotype-sapconcur')} id={sapConcurLogoId} />
                        </>
                    )}

                    {partnerLogo && <div dangerouslySetInnerHTML={{ __html: partnerLogo }} />}
                </div>
            </section>
            <section className={sectionClasses} data-test="app-footer-links">
                <div className={classnames(`${CSS_BLOCK}__items`)}>
                    {showPrivacyStatementLink && (
                        <Link
                            className={classnames(`${CSS_BLOCK}__link`)}
                            href={getPrivacyPolicyUrl()}
                            rel="noreferrer noopener"
                            target="_blank"
                        >
                            {formatter.formattedMessage({ id: 'CoreUI.processorPrivacyStatement' })}
                        </Link>
                    )}

                    {isTravelUser && (
                        <Link
                            className={classnames(`${CSS_BLOCK}__link`)}
                            data-height="540"
                            data-name="policy"
                            data-toggle="popupwindow"
                            data-url="/twPopup/travel_rules_popup.asp"
                            data-width="600"
                            href="/twPopup/travel_rules_popup.asp"
                            target="policy"
                        >
                            {formatter.formattedMessage({ id: 'CoreUI.travelPolicy' })}
                        </Link>
                    )}

                    <Link
                        className={classnames(`${CSS_BLOCK}__link`)}
                        href={getServiceStatusUrl()}
                        target="open"
                    >
                        {formatter.formattedMessage({ id: `CoreUI.serviceStatus_${!dataCenter ? 'all' : dataCenter}` })}
                    </Link>

                    {supportLink && (
                        <Link
                            className={classnames(`${CSS_BLOCK}__link`)}
                            href={supportLink}
                        >
                            {formatter.formattedMessage({ id: 'CoreUI.contactSupport' })}
                        </Link>
                    )}

                    {showCookieConsent && formatter?.lang && (
                        <CookiePreferences
                            country={country}
                            demo={demo}
                            lang={lang}
                        />
                    )}
                </div>
                <div className={classnames(`${CSS_BLOCK}__items`)}>
                    {showChinaLicense && (
                        <ChinaICP classNameMap={classNameMap} cssBlock={CSS_BLOCK} />
                    )}

                    {(lastLogin || lastSigninDateString) && (
                        <LastLogin
                            classNameMap={classNameMap}
                            cssBlock={CSS_BLOCK}
                            lastLogin={lastLogin}
                            lastSigninDateString={lastSigninDateString}
                        />
                    )}

                    <FooterText classNameMap={classNameMap} cssBlock={CSS_BLOCK}>
                        {formatter.formattedMessage({ id: 'CoreUI.copyright', values: { year: new Date().getFullYear() } })}
                    </FooterText>
                </div>
            </section>

            {showOuttaskDevBar && (
                <section className={sectionClasses} data-test="app-footer-devbar">
                    <iframe
                        className={classnames(`${CSS_BLOCK}__devbar`)}
                        frameBorder="0"
                        id="devBarFrame"
                        scrolling="no"
                        src="/devbar.asp"
                        title="devbar"
                    />
                </section>
            )}
        </div>
    );
};

Footer.displayName = 'Footer';

Footer.cssBlock = CSS_BLOCK;

Footer.propTypes = {
    country: PropTypes.string,
    dataCenterLocation: PropTypes.string,
    demo: PropTypes.bool,
    isGov: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    isTravelUser: PropTypes.bool,
    lang: PropTypes.string,
    lastLogin: PropTypes.string, // Deprecated -- use lastSigninDateString instead
    lastSigninDateString: PropTypes.string,
    location: PropTypes.object,
    partnerLogo: PropTypes.string,
    showOuttaskDevBar: PropTypes.bool,
    stretchContent: PropTypes.bool,
    supportLink: PropTypes.string,
    useFullWidth: PropTypes.bool,
    useSmallViewportBreakpoints: PropTypes.bool,
};

Footer.defaultProps = {
    lang: 'en',
    showOuttaskDevBar: false,
};

export default withThemeStyles(compose(
    withNuiFormatter,
    withErrorBoundary,
)(Footer), styles);
